/**
 * German translations (default language).
 *
 * Every key present here MUST also exist in `en.ts` with the same shape.
 * The shared `Translations` interface (imported from `./index`) enforces this
 * at compile time — if you add a key here and forget it in `en.ts`, `tsc`
 * will error.
 *
 * Placeholder syntax: use `{{name}}` for interpolation.
 *   e.g. `"itemDeleted": "{{name}} gelöscht"` → t('itemDeleted', { name: 'Milch' })
 */

import type { Translations } from './index';

const de: Translations = {
  // ── App-level ──────────────────────────────────────────────────────
  'app.loading':                          'Laden\u2026',
  'app.syncing':                          'Wird synchronisiert\u2026',
  'app.undo':                             'Rückgängig',
  'app.restored':                         'Wiederhergestellt',
  'app.deleteFailedRestored':             'Fehler beim Löschen — wiederhergestellt',
  'app.nameRequired':                     'Name erforderlich',
  'app.bugReport':                        'Bug melden',
  'app.bugTitle':                         'Titel',
  'app.bugTitlePlaceholder':              'Kurze Beschreibung des Problems',
  'app.bugDescription':                   'Beschreibung',
  'app.bugDescPlaceholder':               'Was ist passiert? Was hast du erwartet?',
  'app.bugScreenshot':                    'Screenshot anhängen (kann sichtbare Einladungscodes enthalten)',
  'app.bugAutoContext':                   'Auto-Kontext',
  'app.bugLastActions':                   'Letzte Aktionen',
  'app.bugSubmit':                        'Bug melden',
  'app.bugSending':                       'Wird gesendet\u2026',
  'app.bugEnterTitle':                    'Bitte Titel eingeben',
  'app.bugThanks':                        'Danke!',
  'app.bugCreated':                       'Dein Bug-Report wurde direkt erstellt (Issue #{{number}}).',
  'app.bugViewOnGitHub':                  'Auf GitHub ansehen',
  'app.bugServerNotConfigured':           'Bug-Reporting ist serverseitig noch nicht eingerichtet — öffne GitHub manuell.',
  'app.bugError':                         'Fehler beim Erstellen des Bug-Reports',
  'app.householdNotFound':                'Haushalt nicht gefunden oder Zugriff verweigert',
  'app.connectionError':                  'Verbindungsfehler — bitte erneut versuchen',
  'app.householdCreated':                 'Haushalt erstellt',
  'app.householdCreateError':             'Fehler beim Erstellen',
  'app.householdJoined':                  'Haushalt beigetreten',
  'app.invalidCode':                      'Ungültiger Code',
  'app.nameSaved':                        'Name gespeichert',
  'app.noActions':                        'Keine Aktionen aufgezeichnet',

  // ── Tab bar ────────────────────────────────────────────────────────
  'nav.home':                             'Home',
  'nav.rooms':                            'Räume',
  'nav.people':                           'Leute',
  'nav.household':                        'Haushalt',
  'nav.add':                              'Hinzufügen',

  // ── Home view ──────────────────────────────────────────────────────
  'home.greet.night':                     'Noch wach?',
  'home.greet.morning':                   'Guten Morgen',
  'home.greet.afternoon':                 'Guten Tag',
  'home.greet.evening':                   'Guten Abend',
  'home.headline.ok':                     'Alles im grünen Bereich',
  'home.headline.one':                    'Eine Sache braucht deine Aufmerksamkeit',
  'home.headline.n':                      '{{count}} Dinge brauchen deine Aufmerksamkeit',
  'home.sub.ok':                          'Kein Handlungsbedarf gerade — genieß die Ruhe.',
  'home.sub.n':                           'Wisch nach rechts zum Erledigen, nach links zum Verschieben.',
  'home.tourStart':                       'Tour starten',
  'home.scan':                            'Scannen',
  'home.task':                            'Aufgabe',
  'home.expense':                         'Ausgabe',
  'home.overflow':                        'Außerdem offen',
  'home.stackEmpty':                      'Nichts Dringendes gerade',
  'home.swipeLater':                      'Später',
  'home.swipeDone':                       'Erledigt',

  // Feed action labels (home view swipe cards)
  'feed.action.expiring':                 'Verbraucht',
  'feed.action.lowstock':                 'Einkaufen',
  'feed.action.predictedLow':             'Einkaufen',
  'feed.action.task':                     'Erledigt',
  'feed.action.balance':                  'Ausgleichen',

  // ── Feed items (from feed.ts) ──────────────────────────────────────
  'feed.expired.n':                       'Seit {{days}} Tag{{plural}} abgelaufen',
  'feed.expiring.today':                  'Läuft heute ab',
  'feed.expiring.n':                      'Läuft in {{days}} Tag{{plural}} ab',
  'feed.lowstock':                        'Nur noch {{total}} von {{threshold}} vorrätig',
  'feed.predicted.tomorrow':              'Voraussichtlich morgen leer',
  'feed.predicted.n':                     'Reicht voraussichtlich noch {{days}} Tage',
  'feed.task.overdue':                    'Überfällig',
  'feed.task.today':                      'Heute fällig',
  'feed.task.n':                          'In {{days}} Tag{{plural}} fällig',
  'feed.balance.youOwe':                  'Du schuldest {{name}}',
  'feed.balance.owesYou':                 '{{name}} schuldet dir',

  // ── Household view — welcome / onboarding ──────────────────────────
  'hh.welcomeTagline':                    'Dein Haushalt, endlich synchron: Vorrat, Einkauf, Aufgaben und Finanzen an einem Ort.',
  'hh.featureStock':                      'Vorrat tracken',
  'hh.featureShopping':                   'Einkäufe planen',
  'hh.featureTasks':                      'Aufgaben teilen',
  'hh.featureFinances':                   'Kosten splitten',
  'hh.createLabel':                       'Haushalt erstellen',
  'hh.createPlaceholder':                 'Name (z. B. WG Mitte)',
  'hh.createButton':                      'Neuen Haushalt starten',
  'hh.joinLabel':                         'Mit Code beitreten',
  'hh.joinPlaceholder':                   '8-stelliger Code',
  'hh.paste':                             'Einfügen',
  'hh.joinButton':                        'Haushalt beitreten',
  'hh.restoreTitle':                      'Account von anderem Gerät wiederherstellen',
  'hh.restoreUserId':                     'User-ID',
  'hh.restorePlaceholder':                'User-ID einfügen',
  'hh.restoreButton':                     'Gerät verbinden',
  'hh.restoreHint':                       'Deine User-ID findest du im Haushalt-Menü unter „Account".',

  // ── Household view — member view ───────────────────────────────────
  'hh.invite':                            'Einladen',
  'hh.inviteCodeLabel':                   'Code für Mitbewohner:',
  'hh.copyCode':                          'Code kopieren',
  'hh.copyLink':                          'Link kopieren',
  'hh.codeCopied':                        'Code kopiert',
  'hh.linkCopied':                        'Link kopiert',
  'hh.locations':                         'Lagerorte',
  'hh.newLocation':                       'Neuer Ort',
  'hh.noLocations':                       'Keine Orte definiert (z. B. Küche, Kühlschrank)',
  'hh.members':                           'Mitglieder',
  'hh.joinedSince':                       'Dabei seit {{date}}',
  'hh.you':                               '(Du)',
  'hh.removeMember':                      'Entfernen',
  'hh.profile':                           'Dein Profil',
  'hh.profileName':                       'Dein Name im Haushalt',
  'hh.accountRecovery':                   'Account-Wiederherstellung:',
  'hh.saveUserId':                        'Falls du das Gerät wechselst, speichere deine User-ID:',
  'hh.copyUserId':                        'Kopieren',
  'hh.classicView':                       'Klassische Ansicht',
  'hh.classicDescription':                'Die dichteren Power-User-Ansichten (Kategorien, Preise, Nährwerte, Wiederholungen, Aufteilungsregeln, Zahlungshistorie) sind weiterhin verfügbar:',
  'hh.leaveHousehold':                    'Haushalt verlassen',
  'hh.confirmLeave':                      'Haushalt wirklich verlassen?',
  'hh.confirmRemove':                     '{{name}} wirklich aus dem Haushalt entfernen?',
  'hh.memberRemoved':                     '{{name}} entfernt',
  'hh.memberRemoveError':                 'Fehler beim Entfernen',

  // ── Household — locations ──────────────────────────────────────────
  'hh.addSubLocation':                    'Unterort hinzufügen',
  'hh.rename':                            'Umbenennen',
  'hh.delete':                            'Löschen',
  'hh.locationAddTitle':                  'Ort hinzufügen',
  'hh.subLocationAddTitle':               'Unterort hinzufügen',
  'hh.locationAdded':                     'Ort hinzugefügt',
  'hh.subLocationAdded':                  'Unterort hinzugefügt',
  'hh.locationAddError':                  'Fehler beim Anlegen',
  'hh.locationRenameTitle':               'Umbenennen',
  'hh.locationRenamed':                   'Umbenannt',
  'hh.locationRenameError':               'Fehler beim Umbenennen',
  'hh.locationDeleteWithChildren':        '"{{name}}" und alle Unterorte darin wirklich löschen? Artikel darin werden nicht gelöscht, aber verlieren ihren Ort.',
  'hh.locationDelete':                    '"{{name}}" wirklich löschen? Artikel darin werden nicht gelöscht, aber verlieren ihren Ort.',
  'hh.locationDeleted':                   'Gelöscht',
  'hh.locationDeleteError':               'Fehler beim Löschen',

  // ── Household — clipboard / account ────────────────────────────────
  'hh.clipboardEmpty':                    'Zwischenablage ist leer',
  'hh.inserted':                          'Eingefügt',
  'hh.insertNotAllowed':                  'Einfügen nicht erlaubt — bitte Feld lange antippen und einfügen',
  'hh.codeRequired':                      'Code erforderlich',
  'hh.enterUserId':                       'Bitte User-ID eingeben',
  'hh.userIdCopied':                      'User-ID kopiert',
  'hh.copyFailed':                        'Kopieren fehlgeschlagen — User-ID antippen und manuell kopieren',
  'hh.accountRestored':                   'Account wiederhergestellt',
  'hh.accountRestoredNoHousehold':        'Account wiederhergestellt (kein Haushalt gefunden)',
  'hh.accountRestoredReload':             'Account wiederhergestellt — Seite neu laden',
  'hh.newCodeGenerated':                  'Neuer Code generiert',
  'hh.error':                             'Fehler',

  // ── Rooms view ─────────────────────────────────────────────────────
  'rooms.title':                          'Räume',
  'rooms.newRoom':                        'Neuer Raum',
  'rooms.search':                         'Artikel suchen\u2026',
  'rooms.addRoom':                        'Raum hinzufügen',
  'rooms.createFirst':                    'Ersten Raum anlegen',
  'rooms.noRooms':                        'Noch keine Räume angelegt.',
  'rooms.items':                          '{{count}} Artikel',
  'rooms.areas':                          'Bereiche',
  'rooms.addArea':                        'Bereich hinzufügen',
  'rooms.itemsHere':                      'Artikel hier',
  'rooms.noItems':                        'Keine Artikel direkt in {{name}}',
  'rooms.noItemsHere':                    'Keine Artikel hier',
  'rooms.low':                            'niedrig',
  'rooms.move':                           'Verschieben',
  'rooms.noResults':                      'Keine Treffer',
  'rooms.noLocation':                     'Kein Ort zugewiesen',
  'rooms.nameRequired':                   'Name erforderlich',
  'rooms.areaAdded':                      'Bereich hinzugefügt',
  'rooms.roomAdded':                      'Raum hinzugefügt',
  'rooms.addError':                       'Fehler beim Anlegen',

  // ── Rooms — move item modal ────────────────────────────────────────
  'rooms.moveItem':                       '{{name}} verschieben',
  'rooms.moveFrom':                       'Von {{path}} ({{count}} verfügbar)',
  'rooms.moveQty':                        'Menge',
  'rooms.moveTo':                         'Neuer Ort',
  'rooms.moveConfirm':                    'Verschieben',
  'rooms.moveQtyError':                   'Menge muss größer als 0 sein',
  'rooms.moveSameLocation':               'Zielort entspricht dem aktuellen Ort',
  'rooms.movePartial':                    'Nur {{moved}} von {{qty}} verschoben (nicht genug Bestand)',
  'rooms.moved':                          'Verschoben',
  'rooms.moveError':                      'Fehler beim Verschieben',

  // ── People view ────────────────────────────────────────────────────
  'people.title':                         'Leute',
  'people.newTask':                       'Neue Aufgabe',
  'people.balanceTitle':                  'Bilanz im Haushalt',
  'people.allBalanced':                   'Alle Konten sind ausgeglichen',
  'people.settleAll':                     'Alle Schulden ausgleichen',
  'people.doneThisWeek':                  'Erledigt diese Woche',
  'people.nothingThisWeek':               'Diese Woche wurde noch nichts abgehakt',
  'people.members':                       'Mitglieder',
  'people.offen':                         '{{count}} offen',
  'people.thisWeek':                      '{{count}} diese Woche',
  'people.owes':                          'schuldet',
  'people.gets':                          'bekommt',

  // ── People — person detail modal ───────────────────────────────────
  'people.owesHousehold':                 'schuldet dem Haushalt',
  'people.getsHousehold':                 'bekommt vom Haushalt',
  'people.balanced':                      'Ausgeglichen',
  'people.settleWith':                    'Mit {{name}} ausgleichen',
  'people.openTasks':                     'Offene Aufgaben',
  'people.noOpenTasks':                   'Keine offenen Aufgaben',
  'people.done':                          'Erledigt',
  'people.doneRecently':                  '{{count}} kürzlich erledigt',
  'people.fairness':                      'Fairness',
  'people.thisWeekLabel':                 'Diese Woche',
  'people.totalLabel':                    'Insgesamt',

  // ── Brief / Today view ─────────────────────────────────────────────
  'brief.today':                          'Heute',
  'brief.addExpense':                     'Ausgabe hinzufügen',
  'brief.nNeedAttention':                 '{{count}} Dinge brauchen Aufmerksamkeit',
  'brief.allQuiet':                       'Alles ruhig im Haushalt',
  'brief.importantToday':                 'Hier ist, was heute wichtig ist.',
  'brief.noUrgent':                       'Keine dringenden Vorräte, Aufgaben oder Finanzen offen.',
  'brief.quickTask':                      'Aufgabe',
  'brief.quickShopping':                  'Einkaufen',
  'brief.quickExpense':                   'Ausgabe',
  'brief.expiringTitle':                  'MHD / Verbrauchen',
  'brief.noExpiring':                     'Nichts läuft bald ab',
  'brief.suggestionsTitle':               'Einkaufsvorschläge',

  // ── Capture sheet ──────────────────────────────────────────────────
  'capture.title':                        'Was möchtest du tun?',
  'capture.shoppingTrip':                 'Einkaufstour starten',
  'capture.shoppingTripSub':              'Liste abhaken & Preise loggen',
  'capture.scan':                         'Scannen',
  'capture.scanSub':                      'Barcode scannen',
  'capture.manual':                       'Manuell hinzufügen',
  'capture.task':                         'Aufgabe',
  'capture.taskSub':                      'Neue Aufgabe anlegen',
  'capture.toList':                       'Zur Liste',
  'capture.toListSub':                    'Ohne Tour hinzufügen',

  // ── Language setting ───────────────────────────────────────────────
  'settings.language':                    'Sprache',
  'settings.lang.de':                     'Deutsch',
  'settings.lang.en':                     'English',

  // ── Generic actions ────────────────────────────────────────────────
  'feed.action.budget':                    'Ansehen',
  'feed.budget.title':                     'Budget-Warnung: {{category}}',
  'feed.budget.exceeded':                  'Budget überschritten ({{spent}} € von {{budget}} €)',
  'feed.budget.projected':                 'Hochgerechnet ca. {{projected}} € (Budget: {{budget}} €)',
  'hh.exportData':                         'Daten exportieren (JSON)',
  'hh.deleteAccount':                      'Konto löschen',
  'rooms.expiryCheck':                     'Check MHD',
  'rooms.expired':                         'Abgelaufen',
  'rooms.days':                            '{{count}} Tage',
  'rooms.noFixedPlace':                    'Ohne festen Ort',
  'rooms.takeOne':                         'Eine Einheit entnehmen',
  'rooms.addStock':                        'Bestand hinzufügen',
  'rooms.inParent':                        'In {{name}}',
  'rooms.name':                            'Name',
  'rooms.placeRoom':                       'z. B. Küche',
  'rooms.placeArea':                       'z. B. Kühlschrank',
  'rooms.noPlace':                         'Kein Ort',

  'home.tasksTitle':                       'Aufgaben',
  'home.viewAllTasks':                     'Alle Aufgaben ansehen',
  'capture.shoppingList':                  'Einkaufsliste ansehen',
  'capture.allTasks':                      'Alle Aufgaben',
  'capture.allTasksSub':                   'Komplette Aufgabenliste',
  'capture.item':                          'Artikel',
  'capture.expense':                       'Ausgabe',
  'tasks.title':                           'Aufgaben',
  'tasks.new':                             'Neue Aufgabe',
  'tasks.open':                            'Offen',
  'tasks.done':                            'Erledigt',
  'tasks.daily':                           'Täglich',
  'tasks.weekly':                          'Wöchentlich',
  'tasks.monthly':                         'Monatlich',
  'tasks.irregular':                       'Nach Bedarf',
  'tasks.rotation':                        ' (Rotation)',
  'tasks.free':                            'Frei · wer Zeit hat',
  'tasks.markDone':                        '{{title}} erledigen',
  'tasks.reopen':                          '{{title}} wieder öffnen',
  'tasks.delete':                          '{{title}} löschen',
  'tasks.allDone':                         'Alles erledigt!',
  'tasks.edit':                            'Aufgabe bearbeiten',
  'tasks.fieldTitle':                      'Titel',
  'tasks.titlePlaceholder':                'Was ist zu tun?',
  'tasks.description':                     'Beschreibung',
  'tasks.descPlaceholder':                 'Details...',
  'tasks.assignee':                        'Zugewiesen an',
  'tasks.nobody':                          'Niemand',
  'tasks.due':                             'Fällig am',
  'tasks.recurrence':                      'Wiederholung',
  'tasks.once':                            'Einmalig',
  'tasks.irregularLong':                   'Nach Bedarf (unregelmäßig, für alle offen)',
  'tasks.irregularHint':                   'Für Aufgaben ohne festen Rhythmus (z. B. Wäsche waschen) -- bleibt unzugewiesen und offen für alle, wird nach Erledigung nicht neu zugeteilt, sondern springt einfach wieder auf "offen" zurück, sobald es wieder nötig ist.',
  'tasks.rotationTeam':                    'Team-Rotation (Wer wechselt sich ab?)',
  'tasks.checklist':                       'Checkliste (Unteraufgaben)',
  'tasks.addStep':                         'Schritt hinzufügen',
  'tasks.create':                          'Aufgabe erstellen',
  'tasks.saveChanges':                     'Änderungen speichern',
  'tasks.titleRequired':                   'Titel erforderlich',
  'tasks.created':                         'Aufgabe erstellt',
  'tasks.createError':                     'Fehler beim Erstellen',
  'tasks.saved':                           'Gespeichert',
  'tasks.saveError':                       'Fehler beim Speichern',
  'tasks.updateError':                     'Fehler beim Aktualisieren',
  'tasks.irregularCompleted':              'Erledigt! Aufgabe ist wieder offen, sobald sie erneut nötig ist.',
  'tasks.recurringNext':                   'Wiederholende Aufgabe für nächste Runde fällig gestellt!',
  'tasks.recurringChecklistNext':          'Wiederholende Aufgabe für nächste Runde fällig gestellt! (Checkliste zurückgesetzt)',
  'tasks.noChecklist':                     'Keine Checklisten-Punkte',
  'tasks.stepPlaceholder':                 'Schritt (z. B. Boden wischen)',
  'tasks.remove':                          'Entfernen',

  'expenses.cat.groceries':                'Lebensmittel',
  'expenses.cat.rent':                     'Miete & Wohnen',
  'expenses.cat.household':                'Haushalt & Drogerie',
  'expenses.cat.leisure':                  'Freizeit',
  'expenses.cat.settlement':               'Schuldenausgleich',
  'expenses.cat.sonstiges':                'Sonstiges',
  'expenses.title':                        'Finanzen',
  'expenses.paymentHistory':               'Zahlungshistorie',
  'expenses.openPaymentHistory':           'Zahlungshistorie öffnen',
  'expenses.add':                          'Ausgabe hinzufügen',
  'expenses.settle':                       'Ausgleichen',
  'expenses.youOwe':                       'Du schuldest {{name}}',
  'expenses.owesYou':                      '{{name}} schuldet dir',
  'expenses.youPay':                       'Von dir zu zahlen',
  'expenses.owedToYou':                    'Dir wird Geld geschuldet',
  'expenses.allBalanced':                  'Du bist mit allen ausgeglichen',
  'expenses.list':                         'Ausgaben',
  'expenses.paidBy':                       'Bezahlt von {{name}}',
  'expenses.delete':                       'Löschen',
  'expenses.deleteItem':                   '{{title}} löschen',
  'expenses.none':                         'Noch keine Ausgaben',
  'expenses.dateUnknown':                  'Datum unbekannt',
  'budgets.title':                         'Budgets diesen Monat',
  'budgets.short':                         'Budget',
  'budgets.spentOf':                       '{{spent}} € von {{budget}} €',
  'budgets.remaining':                     '{{amount}} € übrig',
  'budgets.over':                          '{{amount}} € drüber',
  'budgets.edit':                          'Budget bearbeiten',
  'budgets.none':                          'Noch keine Monatsbudgets gesetzt',
  'budgets.add':                           'Budget hinzufügen',
  'budgets.category':                      'Kategorie',
  'budgets.monthly':                       'Monatsbudget (€)',
  'budgets.remove':                        'Budget entfernen',
  'budgets.validRequired':                 'Bitte gültiges Budget eingeben',
  'budgets.saved':                         'Budget gespeichert',
  'budgets.saveError':                     'Fehler beim Speichern',
  'budgets.removed':                       'Budget entfernt',
  'budgets.removeError':                   'Fehler beim Entfernen',
  'settle.history':                        'Ausgleichshistorie',
  'settle.none':                           'Noch keine Schuldenausgleiche verbucht',
  'settle.settledExpenses':                'Beglichene Ausgaben',
  'settle.noneSettled':                    'Noch keine Ausgaben durch Ausgleich erledigt',
  'settle.title':                          'Schulden ausgleichen',
  'settle.success':                        'Schulden erfolgreich ausgeglichen!',
  'settle.error':                          'Fehler beim Ausgleichen',
  'settle.instructions':                   'Um alle Konten auf 0,00 € zu setzen, sind folgende Überweisungen nötig:',
  'settle.markPaid':                       'Als bezahlt markieren (Ausgleich verbuchen)',
  'settle.expenseTitle':                   'Schuldenausgleich: {{from}} → {{to}}',
  'expense.edit':                          'Ausgabe bearbeiten',
  'expense.add':                           'Ausgabe hinzufügen',
  'expense.fieldTitle':                    'Titel',
  'expense.titlePlaceholder':              'z. B. Wocheneinkauf',
  'expense.category':                      'Kategorie',
  'expense.saveChanges':                   'Änderungen speichern',
  'expense.create':                        'Ausgabe erstellen',
  'expense.created':                       'Ausgabe erstellt',
  'expense.updated':                       'Ausgabe aktualisiert',
  'expense.saveError':                     'Fehler beim Speichern',
  'expense.updateError':                   'Fehler beim Aktualisieren',

  'capture.tripSub':                       'Liste abhaken & Preise loggen',
  'capture.receipt':                       'Beleg scannen',
  'capture.receiptSub':                    'Foto vom Kassenbon',
  'capture.addToList':                     'Zur Liste',
  'capture.addToListSub':                  'Ohne Tour hinzufügen',
  'capture.shoppingListSub':               'Liste bearbeiten, ohne Tour',
  'shopping.aisle.obst':                   'Obst & Gemüse',
  'shopping.aisle.gemuese':                'Obst & Gemüse',
  'shopping.aisle.milch':                  'Kühlregal & Milch',
  'shopping.aisle.proteine':               'Fleisch & Proteine',
  'shopping.aisle.getreide':               'Getreide & Beilagen',
  'shopping.aisle.getraenke':              'Getränke',
  'shopping.aisle.fette':                  'Öle & Fette',
  'shopping.aisle.fertig':                 'Konserven & Fertigwaren',
  'shopping.aisle.sonstiges':              'Sonstiges & Haushalt',
  'shopping.title':                        'Einkaufen',
  'shopping.scanBarcode':                  'Barcode scannen',
  'shopping.addManual':                    'Manuell hinzufügen',
  'shopping.openByAisle':                  'Offen (nach Supermarkt-Regal)',
  'shopping.suggestion':                   'Vorschlag',
  'shopping.lowStockSuggestion':           'Unter Mindestbestand (+{{count}} empfohlen)',
  'shopping.boughtLog':                    'Gekauft (Menge/MHD/Preis loggen)',
  'shopping.dismissSuggestion':            'Vorschlag ausblenden',
  'shopping.delete':                       'Löschen',
  'shopping.empty':                        'Nichts auf der Liste',
  'shopping.done':                         'Erledigt',
  'shopping.reopen':                       '{{name}} wieder öffnen',
  'shopping.deleteItem':                   '{{name}} löschen',
  'shopping.addToListTitle':               'Zur Liste hinzufügen',
  'shopping.item':                         'Artikel',
  'shopping.needPlaceholder':              'Was wird gebraucht?',
  'shopping.quantityOptional':             'Menge (optional)',
  'shopping.qtyPlaceholder':               'z. B. 2 Packungen',
  'shopping.priceOptional':                'Preis (€, optional)',
  'shopping.pricePlaceholder':             'z. B. 1.99',
  'shopping.add':                          'Hinzufügen',
  'inventory.cat.sonstiges':               'Sonstiges',
  'inventory.cat.getraenke':               'Getränke',
  'inventory.cat.getreide':                'Getreide',
  'inventory.cat.gemuese':                 'Gemüse',
  'inventory.cat.obst':                    'Obst',
  'inventory.cat.milch':                   'Milch',
  'inventory.cat.proteine':                'Proteine',
  'inventory.cat.fette':                   'Fette',
  'inventory.cat.fertig':                  'Fertig',

  'capture.itemSub':                       'Manuell hinzufügen',
  'capture.expenseSub':                    'Kosten festhalten',
  'expense.amount':                        'Betrag (€)',
  'expense.paidBy':                        'Bezahlt von',
  'expense.split':                         'Aufteilung',
  'expense.splitEqual':                    'Gleichmäßig',
  'expense.splitPercent':                  'Prozentual (%)',
  'expense.splitAmounts':                  'Beträge (€)',
  'expense.saveDefaultSplit':              'Als Standard für Kategorie speichern',
  'expense.total':                         'Summe: {{value}}',
  'expense.defaultLoaded':                 'Standard-Aufteilung für Kategorie geladen',
  'expense.defaultSaved':                  'Standard-Aufteilung (%) für Kategorie gespeichert!',
  'expense.validRequired':                 'Bitte gültige Daten eingeben',
  'expense.onePersonRequired':             'Mindestens eine Person auswählen',

  'realtime.connected':                    'Live-Sync verbunden',
  'realtime.connecting':                   'Live-Sync verbindet…',
  'realtime.fallback':                     'Live-Sync nicht verbunden — Polling aktiv',
  'realtime.disconnected':                 'Live-Sync getrennt',
  'presence.shoppingOne':                  '{{name}} ist gerade einkaufen',
  'presence.shoppingMany':                 '{{names}} sind gerade einkaufen',
  'presence.shoppingAlsoOne':              '{{name}} ist auch gerade einkaufen',
  'presence.shoppingAlsoMany':             '{{names}} sind auch gerade einkaufen',

  'trip.close':                            'Einkaufstour schließen',
  'trip.title':                            'Einkaufstour',
  'trip.finish':                           'Beenden',
  'trip.barcode':                          'Barcode',
  'trip.noBarcode':                        'Ohne Barcode',
  'trip.empty':                            'Noch nichts in dieser Tour. Scanne einen Artikel oder füge etwas ohne Barcode hinzu.',
  'trip.finishWithOpen':                   'Einkauf beenden ({{count}} offen lassen)',
  'trip.ended':                            'Einkauf beendet',
  'trip.checkedItems':                     'Aus den abgehakten Artikeln',
  'trip.paidAtCheckout':                   'Gezahlt an der Kasse (€)',
  'trip.discountHint':                     'Hier kannst du Rabatte oder Kassendifferenzen korrigieren, bevor es in die Ausgaben geht.',
  'trip.boughtOpen':                       '{{done}} gekauft · {{open}} bleiben offen',
  'trip.logExpense':                       'Als Ausgabe verbuchen',
  'trip.back':                             'Zurück zur Tour',
  'trip.closeWithoutExpense':              'Ohne Ausgabe schließen',
  'trip.quantity':                         'Menge',
  'trip.pricePaid':                        'Preis gezahlt (€)',
  'trip.checkContinue':                    'Abhaken & weiter',
  'trip.manualAdd':                        'Zusatzkauf hinzufügen',
  'trip.item':                             'Artikel',
  'trip.qtyOptional':                      'Menge (optional)',
  'trip.addToTrip':                        'Zur Tour hinzufügen',
  'trip.nameRequired':                     'Name erforderlich',
  'trip.added':                            'Zur Tour hinzugefügt',
  'trip.addError':                         'Fehler beim Hinzufügen',
  'trip.bookingError':                     'Fehler beim Verbuchen',
  'trip.updateError':                      'Fehler beim Aktualisieren',
  'trip.searching':                        'Suche Produkt...',
  'trip.scanError':                        'Fehler beim Scannen',
  'trip.noAmount':                         'Kein Betrag zum Verbuchen',
  'trip.expenseTitle':                     'Einkaufstour',
  'trip.loggedExpense':                    'Als Ausgabe verbucht',

  'action.save':                          'Speichern',
  'action.cancel':                        'Abbrechen',
};

export default de;
